/**
 * Period calculation utilities for time tracking
 * Supports weekly, biweekly (even/odd), semi-monthly, and monthly periods
 */

import { PERIOD_TYPES } from '@/constants/periods'
import { pbDate } from '@/lib/dates'
import type { PeriodBounds, PeriodConfig, PeriodInfo } from '@/types/periods'

/**
 * Calculate ISO week number (1-53) according to ISO 8601
 * Week 1 is the first week with a Thursday in the new year
 */
export function getISOWeek(date: Date): number {
    const d = new Date(date)
    d.setHours(0, 0, 0, 0)
    // Set to nearest Thursday: current date + 4 - current day number
    // Make Sunday's day number 7
    d.setDate(d.getDate() + 4 - (d.getDay() || 7))
    // Get first day of year
    const yearStart = new Date(d.getFullYear(), 0, 1)
    // Calculate full weeks to nearest Thursday
    const weekNo = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7)
    return weekNo
}

/**
 * Get the start and end dates for a weekly period
 */
function getWeeklyBounds(date: Date, weekStartDay: number): PeriodBounds {
    const d = new Date(date)
    const currentDay = d.getDay()

    // Calculate difference from week start day
    let diff = currentDay - weekStartDay
    if (diff < 0) {
        diff += 7
    }

    // Start of period (week start day at 00:00:00)
    const start = new Date(d)
    start.setDate(d.getDate() - diff)
    start.setHours(0, 0, 0, 0)

    // End of period (6 days later at 23:59:59)
    const end = new Date(start)
    end.setDate(start.getDate() + 6)
    end.setHours(23, 59, 59, 999)

    return {
        start: pbDate(start),
        end: pbDate(end),
    }
}

/**
 * Get the start and end dates for a biweekly period
 */
function getBiweeklyBounds(date: Date, weekStartDay: number, isEvenWeek: boolean): PeriodBounds {
    const d = new Date(date)
    const isoWeek = getISOWeek(d)
    const weekIsEven = isoWeek % 2 === 0

    // Get current week bounds
    const currentWeekBounds = getWeeklyBounds(d, weekStartDay)
    const currentWeekStart = new Date(currentWeekBounds.start)

    // Determine if we need to adjust to the previous or next week
    let periodStart: Date

    if (isEvenWeek === weekIsEven) {
        // Current week matches the desired even/odd pattern, start here
        periodStart = currentWeekStart
    } else {
        // Current week doesn't match, go to previous week
        periodStart = new Date(currentWeekStart)
        periodStart.setDate(currentWeekStart.getDate() - 7)
    }

    // Period is 2 weeks long (14 days)
    const periodEnd = new Date(periodStart)
    periodEnd.setDate(periodStart.getDate() + 13) // 14 days (0-13 inclusive)
    periodEnd.setHours(23, 59, 59, 999)

    return {
        start: pbDate(periodStart),
        end: pbDate(periodEnd),
    }
}

/**
 * Get the start and end dates for a semi-monthly period (1st-15th, 16th-end)
 */
function getSemiMonthlyBounds(date: Date): PeriodBounds {
    const d = new Date(date)
    const day = d.getDate()
    const year = d.getFullYear()
    const month = d.getMonth()

    let start: Date
    let end: Date

    if (day <= 15) {
        // First period: 1st to 15th
        start = new Date(year, month, 1, 0, 0, 0, 0)
        end = new Date(year, month, 15, 23, 59, 59, 999)
    } else {
        // Second period: 16th to end of month
        start = new Date(year, month, 16, 0, 0, 0, 0)
        // Last day of month: day 0 of next month
        end = new Date(year, month + 1, 0, 23, 59, 59, 999)
    }

    return {
        start: pbDate(start),
        end: pbDate(end),
    }
}

/**
 * Get the start and end dates for a monthly period
 */
function getMonthlyBounds(date: Date): PeriodBounds {
    const d = new Date(date)
    const year = d.getFullYear()
    const month = d.getMonth()

    // First day of month at 00:00:00
    const start = new Date(year, month, 1, 0, 0, 0, 0)

    // Last day of month at 23:59:59
    // Day 0 of next month = last day of current month
    const end = new Date(year, month + 1, 0, 23, 59, 59, 999)

    return {
        start: pbDate(start),
        end: pbDate(end),
    }
}

/**
 * Get period bounds for a given date and configuration
 */
export function getPeriodBounds(date: Date = new Date(), config: PeriodConfig): PeriodBounds {
    const { periodType, weekStartDay } = config

    switch (periodType) {
        case 'weekly':
            return getWeeklyBounds(date, weekStartDay)

        case 'biweekly_even':
            return getBiweeklyBounds(date, weekStartDay, true)

        case 'biweekly_odd':
            return getBiweeklyBounds(date, weekStartDay, false)

        case 'semi_monthly':
            return getSemiMonthlyBounds(date)

        case 'monthly':
            return getMonthlyBounds(date)

        default:
            // Fallback to weekly
            return getWeeklyBounds(date, weekStartDay)
    }
}

/**
 * Format a period label for display
 */
export function formatPeriodLabel(periodType: string, start: string, end: string): string {
    const startDate = new Date(start)
    const endDate = new Date(end)

    const formatDate = (date: Date) => {
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        })
    }

    const formatMonthYear = (date: Date) => {
        return date.toLocaleDateString('en-US', {
            month: 'long',
            year: 'numeric',
        })
    }

    switch (periodType) {
        case 'monthly':
            return formatMonthYear(startDate)

        case 'semi_monthly': {
            // Check if this is first or second half
            const day = startDate.getDate()
            const half = day === 1 ? '1st-15th' : '16th-End'
            return `${formatMonthYear(startDate)} (${half})`
        }

        default:
            // For weekly and biweekly, show date range
            return `${formatDate(startDate)} - ${formatDate(endDate)}`
    }
}

/**
 * Get period info including label
 */
export function getPeriodInfo(date: Date = new Date(), config: PeriodConfig): PeriodInfo {
    const bounds = getPeriodBounds(date, config)
    const label = formatPeriodLabel(config.periodType, bounds.start, bounds.end)

    return {
        ...bounds,
        label,
        periodType: config.periodType,
    }
}

/**
 * Get the next period's start date
 */
export function getNextPeriod(date: Date, config: PeriodConfig): Date {
    const bounds = getPeriodBounds(date, config)
    const periodEnd = new Date(bounds.end)

    // Next period starts 1 millisecond after current period ends
    // Then normalize to start of next period
    const nextDay = new Date(periodEnd.getTime() + 1)

    // Get the bounds for that next day to ensure we're at the start
    const nextBounds = getPeriodBounds(nextDay, config)
    return new Date(nextBounds.start)
}

/**
 * Get the previous period's start date
 */
export function getPreviousPeriod(date: Date, config: PeriodConfig): Date {
    const bounds = getPeriodBounds(date, config)
    const periodStart = new Date(bounds.start)

    // Previous period ends 1 millisecond before current period starts
    // Then normalize to start of previous period
    const prevDay = new Date(periodStart.getTime() - 1)

    // Get the bounds for that previous day to ensure we're at the start
    const prevBounds = getPeriodBounds(prevDay, config)
    return new Date(prevBounds.start)
}

/**
 * Get period type display name
 */
export function getPeriodTypeName(periodType: string): string {
    return PERIOD_TYPES[periodType as keyof typeof PERIOD_TYPES] || periodType
}

/**
 * Get helper text for biweekly period types
 * Returns text like "Will start next week" or "Will start in two weeks"
 */
export function getBiweeklyHelperText(periodType: 'biweekly_even' | 'biweekly_odd'): string {
    const currentWeek = getISOWeek(new Date())
    const currentWeekIsEven = currentWeek % 2 === 0

    if (periodType === 'biweekly_even') {
        if (currentWeekIsEven) {
            return 'Period starts this week'
        }
        return 'Period starts next week'
    }

    // biweekly_odd
    if (!currentWeekIsEven) {
        return 'Period starts this week'
    }
    return 'Period starts next week'
}
